import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAdminApi } from "../adminUtils";
import "../admin-base.css";
import "../admin-layout.css";
import "../admin-controls.css";
import "../admin-responsive.css";
import "./formsTab.css";

const FORM_LABELS = { intake: "Pre-therapy questionnaire" };
const labelFor = slug =>
  FORM_LABELS[slug] || String(slug).replace(/[-_]/g, " ").replace(/\b\w/g, c => c.toUpperCase());

const fmtAnswer = a => {
  if (a == null || a === "") return "—";
  if (Array.isArray(a)) return a.length ? a.map(fmtAnswer).join(", ") : "—";
  if (typeof a === "object") return Object.entries(a).map(([k, v]) => `${k}: ${fmtAnswer(v)}`).join("; ");
  return String(a);
};

const fmtDate = iso =>
  new Date(iso).toLocaleString("en-US", {
    timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });

const csvCell = v => `"${String(v ?? "").replace(/"/g, '""')}"`;

export default function FormsTab() {
  const { API, AUTH } = useAdminApi();
  const authRef = useRef(AUTH);
  authRef.current = AUTH;

  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [form, setForm]       = useState("all");
  const [search, setSearch]   = useState("");
  const [open, setOpen]       = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const all = [];
      let page = 1, pages = 1;
      do {
        const res = await fetch(`${API}/forms?limit=200&page=${page}`, { headers: authRef.current });
        if (!res.ok) {
          throw new Error(res.status === 401 || res.status === 403
            ? "Owner access required."
            : `Couldn't load submissions (${res.status}).`);
        }
        const data = await res.json();
        all.push(...(data.items || []));
        pages = data.pages || 1;
        page++;
      } while (page <= pages);
      setItems(all);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  const formTypes = useMemo(() => [...new Set(items.map(i => i.form))].sort(), [items]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return items.filter(i =>
      (form === "all" || i.form === form) &&
      (!s || (i.name || "").toLowerCase().includes(s) || (i.email || "").toLowerCase().includes(s))
    );
  }, [items, form, search]);

  const exportCsv = () => {
    const questions = [];
    filtered.forEach(i => (i.answers || []).forEach(({ q }) => { if (!questions.includes(q)) questions.push(q); }));
    const header = ["Submitted", "Form", "Name", "Email", ...questions];
    const rows = filtered.map(i => {
      const byQ = Object.fromEntries((i.answers || []).map(({ q, a }) => [q, fmtAnswer(a)]));
      return [fmtDate(i.submittedAt), labelFor(i.form), i.name, i.email || "", ...questions.map(q => byQ[q] ?? "")];
    });
    const csv = [header, ...rows].map(r => r.map(csvCell).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `iy-sp-forms-${form}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fm-wrap">
      <div className="fm-toolbar">
        <select className="fm-select" value={form} onChange={e => setForm(e.target.value)}>
          <option value="all">All forms</option>
          {formTypes.map(f => <option key={f} value={f}>{labelFor(f)}</option>)}
        </select>
        <input
          className="fm-search" type="search" placeholder="Search name or email"
          value={search} onChange={e => setSearch(e.target.value)}
        />
        <button className="fm-btn" onClick={load} disabled={loading}>{loading ? "Loading…" : "Refresh"}</button>
        <button className="fm-btn" onClick={exportCsv} disabled={!filtered.length}>Export CSV</button>
      </div>

      {error && <p className="fm-error">{error}</p>}
      {!loading && !error && (
        <p className="fm-count">{filtered.length} submission{filtered.length === 1 ? "" : "s"}</p>
      )}

      <ul className="fm-list">
        {filtered.map(i => {
          const isOpen = open === i._id;
          return (
            <li key={i._id} className={`fm-item ${isOpen ? "open" : ""}`}>
              <button className="fm-row" onClick={() => setOpen(isOpen ? null : i._id)}>
                <span className="fm-date">{fmtDate(i.submittedAt)}</span>
                <span className="fm-form">{labelFor(i.form)}</span>
                <span className="fm-name">{i.name}</span>
                <span className="fm-email">{i.email || "—"}</span>
                <span className="fm-chev">{isOpen ? "▾" : "▸"}</span>
              </button>
              {isOpen && (
                <div className="fm-detail">
                  {i.email && <p className="fm-contact"><a href={`mailto:${i.email}`}>{i.email}</a></p>}
                  <dl className="fm-answers">
                    {(i.answers || []).map(({ id, q, a }, idx) => (
                      <div className="fm-qa" key={id || idx}>
                        <dt>{q}</dt>
                        <dd>{fmtAnswer(a)}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}